/**
 * Insight Templates admin page (SmartImprove).
 *
 * Consulting-grade transformation insight templates aligned with the
 * canonical Business Process + Sub-Process taxonomy. The backend
 * collection is still named `smartimprove_insight_templates` (route slug
 * `/insight-templates` preserved) for backward compatibility — the
 * user-facing label is "Insight Templates".
 *
 * Technical fields (slug, trigger_conditions, initiative_hints,
 * recommendation_mapping arrays) remain in the DB but are hidden from
 * the admin table so the screen reads like a consulting playbook
 * rather than master data maintenance.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import { Plus, Pencil, Trash2 } from "lucide-react";
import {
  insightTemplatesApi,
  operationalAreasApi,
  subProcessesApi,
} from "../../lib/smartimproveAdminApi";

// ----- Canonical option sets (consultant-facing) -----
const INSIGHT_CATEGORIES = [
  "Productivity",
  "Process Standardization",
  "Operational Visibility",
  "Automation Maturity",
  "Inventory Accuracy",
  "Workforce Efficiency",
  "System Adoption",
  "Integration Stability",
  "Exception Management",
  "Scalability Readiness",
  "Warehouse Governance",
  "Future Automation Readiness",
];

const TRANSFORMATION_THEMES = [
  "RF Modernization",
  "Warehouse Standardization",
  "Process Harmonization",
  "Automation Enablement",
  "KPI & Visibility Improvements",
  "Inventory Optimization",
  "Workforce Productivity",
  "Digital Transformation",
  "Operational Stability",
];

const EXECUTIVE_SEVERITY = [
  { value: "critical", label: "Critical" },
  { value: "high", label: "High" },
  { value: "medium", label: "Medium" },
  { value: "low", label: "Low" },
];

const ROLLOUT_WAVE = [
  { value: "", label: "—" },
  { value: "quick_win", label: "Quick Win" },
  { value: "wave_1", label: "Wave 1" },
  { value: "wave_2", label: "Wave 2" },
  { value: "wave_3", label: "Wave 3" },
  { value: "continuous", label: "Continuous" },
];

const SEVERITY_PILL = {
  critical: "bg-[#FEE2E2] text-[#991B1B] border-[#FCA5A5]",
  high: "bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]",
  medium: "bg-[#EFF6FF] text-[#1D4ED8] border-[#BFDBFE]",
  low: "bg-[#F1F5F9] text-[#475569] border-[#E2E8F0]",
};

function slugify(name) {
  return String(name || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 80);
}

const EMPTY = {
  id: null,
  slug: "",
  title: "",
  operational_area: "",
  sub_process_key: "",
  insight_category: "",
  transformation_theme: "",
  severity: "medium",
  narrative: "",
  business_impact: "",
  operational_implication: "",
  executive_summary_snippet: "",
  rollout_wave_hint: "",
  active: true,
  sort_order: 0,
};

function InsightDialog({
  open,
  onOpenChange,
  value,
  processes,
  subProcesses,
  onChange,
  onSave,
  saving,
}) {
  const isEdit = !!value?.id;
  const subOptions = useMemo(
    () =>
      (subProcesses || [])
        .filter(
          (s) =>
            s.active !== false && s.process_key === value?.operational_area
        )
        .sort(
          (a, b) =>
            (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
            String(a.label).localeCompare(String(b.label))
        ),
    [subProcesses, value?.operational_area]
  );
  const set = (patch) => onChange({ ...value, ...patch });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="insight-dialog">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Insight Template" : "New Insight Template"}
          </DialogTitle>
          <DialogDescription>
            Consulting-grade transformation insight used to generate
            findings, executive narratives, and improvement opportunities
            during SmartImprove engagements.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-4 py-2 max-h-[65vh] overflow-y-auto pr-1">
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Insight Template Title
            </label>
            <Input
              value={value?.title || ""}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="e.g. Wave execution maturity varies across sites"
              className="rounded-sm text-xs"
              data-testid="insight-field-title"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Business Process
            </label>
            <Select
              value={value?.operational_area || ""}
              onValueChange={(v) =>
                set({ operational_area: v, sub_process_key: "" })
              }
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="insight-field-process"
              >
                <SelectValue placeholder="Select business process" />
              </SelectTrigger>
              <SelectContent>
                {processes
                  .filter((p) => p.active !== false)
                  .map((p) => (
                    <SelectItem key={p.key} value={p.key}>
                      {p.label}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Sub-Process
            </label>
            <Select
              value={value?.sub_process_key || ""}
              onValueChange={(v) => set({ sub_process_key: v })}
              disabled={!value?.operational_area}
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="insight-field-sub-process"
              >
                <SelectValue
                  placeholder={
                    value?.operational_area
                      ? "Select sub-process (optional)"
                      : "Select business process first"
                  }
                />
              </SelectTrigger>
              <SelectContent>
                {subOptions.map((s) => (
                  <SelectItem key={s.key} value={s.key}>
                    {s.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Insight Category
            </label>
            <Select
              value={value?.insight_category || ""}
              onValueChange={(v) => set({ insight_category: v })}
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="insight-field-category"
              >
                <SelectValue placeholder="Select category" />
              </SelectTrigger>
              <SelectContent>
                {INSIGHT_CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Transformation Theme
            </label>
            <Select
              value={value?.transformation_theme || ""}
              onValueChange={(v) => set({ transformation_theme: v })}
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="insight-field-theme"
              >
                <SelectValue placeholder="Select theme" />
              </SelectTrigger>
              <SelectContent>
                {TRANSFORMATION_THEMES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Executive Severity
            </label>
            <Select
              value={value?.severity || "medium"}
              onValueChange={(v) => set({ severity: v })}
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="insight-field-severity"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {EXECUTIVE_SEVERITY.map((c) => (
                  <SelectItem key={c.value} value={c.value}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Rollout Wave Hint
            </label>
            <Select
              value={value?.rollout_wave_hint || ""}
              onValueChange={(v) => set({ rollout_wave_hint: v })}
            >
              <SelectTrigger
                className="rounded-sm text-xs"
                data-testid="insight-field-rollout"
              >
                <SelectValue placeholder="—" />
              </SelectTrigger>
              <SelectContent>
                {ROLLOUT_WAVE.map((c) => (
                  <SelectItem key={c.value || "__none"} value={c.value || "__none"}>
                    {c.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Insight Narrative
            </label>
            <Textarea
              value={value?.narrative || ""}
              onChange={(e) => set({ narrative: e.target.value })}
              rows={3}
              placeholder="Executive-ready, workshop-ready narrative describing the finding."
              className="rounded-sm text-xs"
              data-testid="insight-field-narrative"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Business Impact
            </label>
            <Textarea
              value={value?.business_impact || ""}
              onChange={(e) => set({ business_impact: e.target.value })}
              rows={2}
              placeholder="How this materially impacts the business — cost, service, risk."
              className="rounded-sm text-xs"
              data-testid="insight-field-business-impact"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Operational Implication
            </label>
            <Textarea
              value={value?.operational_implication || ""}
              onChange={(e) =>
                set({ operational_implication: e.target.value })
              }
              rows={2}
              placeholder="What the operations team sees on the floor today."
              className="rounded-sm text-xs"
              data-testid="insight-field-implication"
            />
          </div>
          <div className="col-span-2">
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Executive Summary Snippet
            </label>
            <Textarea
              value={value?.executive_summary_snippet || ""}
              onChange={(e) =>
                set({ executive_summary_snippet: e.target.value })
              }
              rows={2}
              placeholder="One-line, board-ready takeaway."
              className="rounded-sm text-xs"
              data-testid="insight-field-summary"
            />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="insight-active"
              type="checkbox"
              checked={value?.active !== false}
              onChange={(e) => set({ active: e.target.checked })}
              data-testid="insight-field-active"
            />
            <label htmlFor="insight-active" className="text-xs text-[#374151]">
              Active
            </label>
          </div>
          <div>
            <label className="block text-xs font-medium text-[#374151] mb-1">
              Sort order
            </label>
            <Input
              type="number"
              value={value?.sort_order ?? 0}
              onChange={(e) => set({ sort_order: e.target.value })}
              className="rounded-sm text-xs"
              data-testid="insight-field-sort-order"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
            className="rounded-sm text-xs"
            data-testid="insight-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            disabled={
              saving || !value?.title?.trim() || !value?.operational_area
            }
            className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
            data-testid="insight-save"
          >
            {saving ? "Saving…" : isEdit ? "Save changes" : "Create"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function InsightTemplatesPage() {
  const [rows, setRows] = useState([]);
  const [processes, setProcesses] = useState([]);
  const [subProcesses, setSubProcesses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [filterProcess, setFilterProcess] = useState("__all");

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [items, bps, sps] = await Promise.all([
        insightTemplatesApi.list(),
        operationalAreasApi.list(),
        subProcessesApi.list(),
      ]);
      setRows(items || []);
      setProcesses(bps || []);
      setSubProcesses(sps || []);
    } catch (e) {
      toast.error("Failed to load insight templates");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const processByKey = useMemo(() => {
    const m = {};
    processes.forEach((p) => (m[p.key] = p));
    return m;
  }, [processes]);
  const subByKey = useMemo(() => {
    const m = {};
    subProcesses.forEach((s) => (m[s.key] = s));
    return m;
  }, [subProcesses]);

  const sortedRows = useMemo(() => {
    const filtered =
      filterProcess === "__all"
        ? rows
        : rows.filter((r) => r.operational_area === filterProcess);
    return [...filtered].sort(
      (a, b) =>
        (processByKey[a.operational_area]?.sort_order ?? 999) -
          (processByKey[b.operational_area]?.sort_order ?? 999) ||
        (a.sort_order ?? 0) - (b.sort_order ?? 0) ||
        String(a.title).localeCompare(String(b.title))
    );
  }, [rows, filterProcess, processByKey]);

  const openCreate = () => {
    setForm({ ...EMPTY });
    setDialog(true);
  };
  const openEdit = (row) => {
    setForm({
      id: row.id,
      slug: row.slug || "",
      title: row.title || "",
      operational_area: row.operational_area || "",
      sub_process_key: row.sub_process_key || "",
      insight_category: row.insight_category || "",
      transformation_theme: row.transformation_theme || "",
      severity: row.severity || "medium",
      narrative: row.narrative || "",
      business_impact: row.business_impact || "",
      operational_implication: row.operational_implication || "",
      executive_summary_snippet: row.executive_summary_snippet || "",
      rollout_wave_hint: row.rollout_wave_hint || "",
      active: row.active !== false,
      sort_order: row.sort_order ?? 0,
    });
    setDialog(true);
  };

  const save = async () => {
    const isEdit = !!form.id;
    const title = (form.title || "").trim();
    if (!title || !form.operational_area) return;
    const slug = isEdit ? form.slug : slugify(title);
    const rollout =
      form.rollout_wave_hint === "__none" ? "" : form.rollout_wave_hint || "";
    const payload = {
      slug,
      title,
      operational_area: form.operational_area,
      sub_process_key: form.sub_process_key || "",
      insight_category: form.insight_category || "",
      transformation_theme: form.transformation_theme || "",
      severity: form.severity || "medium",
      narrative: (form.narrative || "").trim(),
      business_impact: (form.business_impact || "").trim(),
      operational_implication: (form.operational_implication || "").trim(),
      executive_summary_snippet: (form.executive_summary_snippet || "").trim(),
      rollout_wave_hint: rollout,
      active: !!form.active,
      sort_order: Number(form.sort_order) || 0,
    };
    setSaving(true);
    try {
      if (isEdit) await insightTemplatesApi.update(form.id, payload);
      else await insightTemplatesApi.create(payload);
      toast.success(`Insight template ${isEdit ? "updated" : "created"}`);
      setDialog(false);
      await reload();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Failed to save");
    } finally {
      setSaving(false);
    }
  };
  const remove = async (row) => {
    if (!window.confirm(`Delete "${row.title}"?`)) return;
    try {
      await insightTemplatesApi.remove(row.id);
      toast.success("Insight template deleted");
      await reload();
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  return (
    <div className="space-y-5" data-testid="si-insight-templates">
      <header className="flex items-start justify-between">
        <div>
          <p className="text-[11px] font-semibold tracking-[0.18em] uppercase text-[#025979]">
            SmartImprove · Intelligence Framework
          </p>
          <h1
            className="text-2xl font-bold text-[#111827] mt-1"
            style={{ fontFamily: "Chivo, sans-serif" }}
          >
            Insight Templates
          </h1>
          <p className="text-sm text-[#6B7280] mt-1 max-w-2xl">
            Consulting-grade operational insight templates used to generate
            transformation findings, executive narratives, and business
            improvement opportunities.
          </p>
        </div>
        <Button
          onClick={openCreate}
          className="bg-[#025979] hover:bg-[#03738C] text-white rounded-sm text-xs"
          data-testid="insight-add-btn"
        >
          <Plus className="w-3.5 h-3.5 mr-1.5" /> Add Insight Template
        </Button>
      </header>

      <div className="flex items-center gap-3">
        <label className="text-xs font-medium text-[#374151]">
          Filter by Business Process
        </label>
        <Select value={filterProcess} onValueChange={setFilterProcess}>
          <SelectTrigger
            className="rounded-sm text-xs w-72"
            data-testid="insight-filter-process"
          >
            <SelectValue placeholder="All" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all">All Business Processes</SelectItem>
            {processes
              .filter((p) => p.active !== false)
              .map((p) => (
                <SelectItem key={p.key} value={p.key}>
                  {p.label}
                </SelectItem>
              ))}
          </SelectContent>
        </Select>
        <span className="text-xs text-[#6B7280] ml-auto">
          {sortedRows.length} template{sortedRows.length === 1 ? "" : "s"}
        </span>
      </div>

      <section className="bg-white border border-[#E5E7EB] rounded-sm overflow-x-auto">
        {loading ? (
          <div className="px-5 py-8 text-sm text-[#6B7280]">Loading…</div>
        ) : sortedRows.length === 0 ? (
          <div
            className="px-5 py-12 text-center text-sm text-[#6B7280]"
            data-testid="insight-empty"
          >
            No insight templates yet. Click{" "}
            <strong>Add Insight Template</strong> to create the first one.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <tr>
                <th className="px-4 py-2 font-semibold">Insight Template</th>
                <th className="px-4 py-2 font-semibold">Business Process</th>
                <th className="px-4 py-2 font-semibold">Sub-Process</th>
                <th className="px-4 py-2 font-semibold">Insight Category</th>
                <th className="px-4 py-2 font-semibold">Executive Severity</th>
                <th className="px-4 py-2 font-semibold">
                  Transformation Theme
                </th>
                <th className="px-4 py-2 font-semibold text-right">Active</th>
                <th className="px-3 py-2" />
              </tr>
            </thead>
            <tbody>
              {sortedRows.map((row) => {
                const bp = processByKey[row.operational_area];
                const sp = subByKey[row.sub_process_key];
                const sev = String(row.severity || "").toLowerCase();
                return (
                  <tr
                    key={row.id}
                    className="border-t border-[#F1F5F9] hover:bg-[#F8FAFC] align-top"
                    data-testid={`insight-row-${row.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="text-[#111827] font-medium">
                        {row.title}
                      </div>
                      {row.executive_summary_snippet || row.narrative ? (
                        <p className="text-[11px] text-[#6B7280] mt-0.5 line-clamp-2 max-w-md">
                          {row.executive_summary_snippet || row.narrative}
                        </p>
                      ) : null}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {bp?.label || <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {sp?.label || <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {row.insight_category || (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {sev ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm capitalize ${
                            SEVERITY_PILL[sev] ||
                            "bg-[#F3F4F6] text-[#374151] border-[#E5E7EB]"
                          }`}
                        >
                          {sev}
                        </span>
                      ) : (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-[#374151]">
                      {row.transformation_theme || (
                        <span className="text-[#9CA3AF]">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      {row.active === false ? (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#F3F4F6] text-[#6B7280] border-[#E5E7EB]">
                          inactive
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 text-[11px] font-semibold border rounded-sm bg-[#ECFDF5] text-[#065F46] border-[#A7F3D0]">
                          active
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => openEdit(row)}
                        className="text-xs font-semibold text-[#025979] hover:text-[#03738C] mr-3"
                        data-testid={`insight-edit-${row.id}`}
                        aria-label={`Edit ${row.title}`}
                      >
                        <Pencil className="inline w-3.5 h-3.5" />
                      </button>
                      <button
                        onClick={() => remove(row)}
                        className="text-xs font-semibold text-[#B91C1C] hover:text-[#7F1D1D]"
                        data-testid={`insight-delete-${row.id}`}
                        aria-label={`Delete ${row.title}`}
                      >
                        <Trash2 className="inline w-3.5 h-3.5" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </section>

      <InsightDialog
        open={dialog}
        onOpenChange={setDialog}
        value={form}
        processes={processes}
        subProcesses={subProcesses}
        onChange={setForm}
        onSave={save}
        saving={saving}
      />
    </div>
  );
}
